import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  Popup,
  PermissionDenied,
  ResponseError,
  responseErrorsResolver,
  ajvResolver,
  ButtonPrimary,
  ButtonPseudo,
  PermissionEnum,
  Account,
  Response,
} from '@ui/src';
import { useAppSelector } from '@store';
import { AccountingJobValues } from './types';
import {
  useCreateAccountingJobMutation,
  useLazyGetAccountingJobsQuery,
  useRestatementAccountingJobMutation,
  useGetAccountingManagmentAccountsCategoriesQuery,
  useLazyGetAccountingManagmentAccountsListQuery,
  useLazyGetAccountingManagmentJobAccountsQuery,
} from './store/service';
import CreateJobForm from '../../components/accounting-managment/create';

import ROUTES from '../../routes';

const schema = {
  type: 'object',
  properties: {
    periodFrom: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Start From is required',
    },
    type: { type: 'string', minLength: 1, errorMessage: 'Type is required' },
  },
  required: ['periodFrom', 'type'],
};

const CreateAccoutingJob = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountsList, setSelectedAccountsList] = useState<Account[]>(
    [],
  );
  const [allAccounts, setAllAccounts] = useState(true);

  const { search, state: locationState } = useLocation();

  const navigate = useNavigate();

  const {
    accountingManagment: { takeCount, accountCategories },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { isLoading: accountsCategoriesLoading } =
    useGetAccountingManagmentAccountsCategoriesQuery(
      'all=true&include=primaryAccounts',
    );

  const [getAccountingJobs] = useLazyGetAccountingJobsQuery();
  const [create, { isLoading, isSuccess }] = useCreateAccountingJobMutation();
  const [
    createRestatement,
    { isLoading: restatementIsLoading, isSuccess: restatementIsSuccess },
  ] = useRestatementAccountingJobMutation();
  const [
    getAccountsList,
    {
      isLoading: accountsListLoading,
      isFetching: accountsListUpdateInProgress,
    },
  ] = useLazyGetAccountingManagmentAccountsListQuery();
  const [getAccountingManagmentJobAccounts] =
    useLazyGetAccountingManagmentJobAccountsQuery();

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    watch,
    setValue,
    reset,
  } = useForm<AccountingJobValues>({ resolver: ajvResolver(schema) });

  const type = watch('type');

  const isDirtyState =
    isDirty || selectedAccountsList?.length || locationState?.duplicate;

  const handleClose = () => {
    setGlobalError('');
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.REPORTS_ACCOUNTING_MANAGMENT_LIST}${search || ''}`);
    }, 100);
  };

  const duplicateAccountingManagmentJobAccountsAction = useCallback(
    async (job: AccountingJobValues) => {
      const body: AccountingJobValues = {
        periodFrom: job.periodFrom,
        type: job.type,
      };

      if (job.accountsIds?.length) {
        setAllAccounts(false);
        const query = `?ids=${job?.accountsIds.join('&ids=')}`;

        const response = await getAccountingManagmentJobAccounts(query);

        if (response?.data?.data) {
          setSelectedAccountsList(
            response?.data?.data?.edges?.map((item) => item.node),
          );
        }

        body.accountsIds = job.accountsIds;
      }

      reset(body);
    },
    [],
  );

  const getAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc&closed=false`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges.map((item) => item.node);

        setAccounts(filteredRes);
      }
    }
  }, []);

  const onSearch = async (searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      const response = (await getAccountsList(
        `?search=${searchText}&all=true&closed=false`,
      )) as { data: Response<Account> };

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push(item.node);
        });
      }

      setAccounts(results);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const periodFrom = new Date(values.periodFrom);

    periodFrom.setHours(12);
    periodFrom.setMinutes(0);
    periodFrom.setSeconds(0);

    const body: AccountingJobValues = {
      periodFrom: periodFrom?.toISOString(),
      type: values.type,
    };

    if (values?.accountsIds) {
      body.accountsIds = values.accountsIds;
    }

    let response;

    if (
      activePermissions?.includes(
        PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
      )
    ) {
      response = (await create(body)) as {
        error?: ResponseError;
        data?: { data: AccountingJobValues };
      };
    } else if (
      activePermissions?.includes(
        PermissionEnum.REPORTS_ACCOUNTING_JOBS_RESTATEMENT_CREATE_ACTION,
      ) &&
      !activePermissions?.includes(
        PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
      )
    ) {
      response = (await createRestatement(body)) as {
        error?: ResponseError;
        data: { data: AccountingJobValues };
      };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (isSuccess || restatementIsSuccess) {
      getAccountingJobs(`${search}&take=${takeCount}`);

      handleClose();
    }
  }, [isSuccess, search, restatementIsSuccess]);

  useEffect(() => {
    if (locationState && locationState.duplicate) {
      duplicateAccountingManagmentJobAccountsAction(
        locationState.accountingJob,
      );
    }
  }, [locationState]);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
    ) &&
    !activePermissions?.includes(
      PermissionEnum.REPORTS_ACCOUNTING_JOBS_RESTATEMENT_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <CreateJobForm
        control={control}
        errors={errors}
        activePermissions={activePermissions}
        accountCategories={accountCategories}
        accountsCategoriesLoading={accountsCategoriesLoading}
        accountsIsLoading={accountsListLoading || accountsListUpdateInProgress}
        onSearchAutocomplete={onSearch}
        setAccountQuery={getAccountsListAction}
        accountsList={accounts}
        setAccounts={setAccounts}
        setValue={setValue}
        selectedAccountsList={selectedAccountsList}
        setSelectedAccountsList={setSelectedAccountsList}
        setAllAccounts={setAllAccounts}
        allAccounts={allAccounts}
        type={type}
      />
    );
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Customers}
        onClose={() => {
          if (isDirtyState) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Add Accounting Job"
        errorSidepanel={globalError}
        sidepanelExpandedKey="create-accounting-job-panel"
        footer={
          <>
            {(activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
            ) ||
              activePermissions?.includes(
                PermissionEnum.REPORTS_ACCOUNTING_JOBS_RESTATEMENT_CREATE_ACTION,
              )) && (
              <ButtonPrimary
                text={
                  isLoading || restatementIsLoading ? 'Loading...' : 'Add Job'
                }
                disabled={!isDirtyState || isLoading || restatementIsLoading}
                onClick={onSubmit}
                bold
              />
            )}

            <ButtonPseudo
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirtyState) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateAccoutingJob;
